import {FC, HTMLAttributes, ReactNode, useEffect, useState} from 'react'
import {
    StyledConfirmationModalAccordionButton,
    StyledConfirmationModalAccordionContainer,
    StyledConfirmationModalLighterParagraph,
    StyledConfirmationModalTitle
} from './style'
import {Button, ButtonProps} from '../button/Button'
import {Flexbox, FlexboxProps} from '../flexbox/FlexBox'
import {Modal, ModalBody, ModalFooter, ModalHeader, ModalOverlay} from '@components/ui/modal-atoms/ModalAtoms.tsx'
import {Divider} from '@components/ui/divider/Divider.tsx'
import {Spinner} from '@components/ui/spinner/Spinner.tsx'
import {Trans, useTranslation} from '@/translations/i18n.tsx'
import {Bell01Icon, ChevronDownIcon, ChevronUpIcon, PlusIcon, XCloseIcon} from '@components/ui/icon/Icon.tsx'
import {Badge} from '@components/ui/badge/Badge.tsx'
import {Avatar} from '@components/ui/avatar/Avatar.tsx'
import {UsersMultiSelectPopover} from '@/features/users/components/users-multi-select-popover/UsersMultiSelectPopover.tsx'
import {TextArea} from '@components/commons/textarea/TextArea.tsx'
import {useFormContext, useWatch} from 'react-hook-form'
import {NotificationUsersConfirmationValidationSchema} from '@/features/users/types.ts'
import {useGetUsers} from '@/features/users/queries/useGetUsers.ts'

export const ConfirmationModalTitle = (props: HTMLAttributes<HTMLHeadingElement>) => (
    <StyledConfirmationModalTitle {...props} />
)
export const ConfirmationModalLighterParagraph = (props: HTMLAttributes<HTMLParagraphElement>) => (
    <StyledConfirmationModalLighterParagraph {...props} />
)
export const ConfirmationModalAccordionButton = (props: ButtonProps) => (
    <StyledConfirmationModalAccordionButton {...props} />
)
export const ConfirmationModalAccordionContainer = (props: FlexboxProps<'div'>) => (
    <StyledConfirmationModalAccordionContainer {...props} />
)

interface ConfirmationModalProps {
    onClose: () => void
    onConfirm: () => void
    isConfirmPending: boolean
    headerSlot: ReactNode
    contentSlot?: ReactNode
}
/**
 * @deprecated use the new ConfirmationDialog instead
 */
export const ConfirmationModal: FC<ConfirmationModalProps> = ({
    headerSlot,
    contentSlot,
    onClose,
    onConfirm,
    isConfirmPending
}) => {
    const {t} = useTranslation()
    const [isAccordionOpen, setIsAccordionOpen] = useState(false)
    const form = useFormContext<NotificationUsersConfirmationValidationSchema>()
    const selectedUsers = useWatch({control: form.control, name: 'notificationsRecipients'})
    const usersQuery = useGetUsers({limit: 10, page: 1})

    useEffect(() => {
        form.setValue('notificationsRecipients', usersQuery?.data ? usersQuery.data.data.slice(0, 4) : [])
    }, [usersQuery.data])

    return (
        <>
            <ModalOverlay onClick={onClose} />
            <Modal width="400px" overflowY="unset">
                <ModalHeader>{headerSlot}</ModalHeader>
                <Divider direction="horizontal" />
                {!!contentSlot && (
                    <>
                        <ModalBody>{contentSlot}</ModalBody>
                        <Divider direction="horizontal" />
                    </>
                )}
                <ModalBody>
                    <ConfirmationModalAccordionButton
                        variant="ghost"
                        fullWidth
                        onClick={() => setIsAccordionOpen(prev => !prev)}
                        shape="text"
                    >
                        <Flexbox gap={2} align="center">
                            <Bell01Icon />
                            <p>
                                <Trans
                                    i18nKey="users:confirmation_modal_notification"
                                    values={{count: selectedUsers?.length || 0}}
                                    components={{b: <b />}}
                                />
                            </p>
                        </Flexbox>
                        {isAccordionOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    </ConfirmationModalAccordionButton>
                    {isAccordionOpen && (
                        <ConfirmationModalAccordionContainer gap={2}>
                            <UsersMultiSelectPopover
                                selectedUserIds={selectedUsers ? selectedUsers.map(user => user.id) : []}
                                toggleClick={user => {
                                    if (selectedUsers?.some(u => u.id == user.id)) {
                                        form.setValue(
                                            'notificationsRecipients',
                                            selectedUsers.filter(u => u.id != user.id)
                                        )
                                    } else {
                                        form.setValue('notificationsRecipients', selectedUsers?.concat(user))
                                    }
                                }}
                                trigger={
                                    <Button variant="secondary" shape="square" size="sm">
                                        <PlusIcon />
                                    </Button>
                                }
                            />

                            {selectedUsers ? (
                                selectedUsers.map(user => (
                                    <Badge size="sm" shape="rectangle" key={user.id}>
                                        <Avatar size="sm" name={user.fullName} />
                                        {user.fullName}
                                        <Button
                                            shape="text"
                                            variant="ghost"
                                            onClick={() => {
                                                form.setValue(
                                                    'notificationsRecipients',
                                                    selectedUsers.filter(u => u.id != user.id)
                                                )
                                            }}
                                        >
                                            <XCloseIcon />
                                        </Button>
                                    </Badge>
                                ))
                            ) : (
                                <p>{t('commons:no_results')}</p>
                            )}

                            <TextArea
                                placeholder={t('users:notes_placeholder')}
                                {...form.register('notificationDetails')}
                            />
                        </ConfirmationModalAccordionContainer>
                    )}
                </ModalBody>
                <Divider direction="horizontal" />
                <ModalFooter>
                    <Button variant="secondary" onClick={onClose} disabled={isConfirmPending}>
                        {t('commons:cancel')}
                    </Button>
                    <Button variant="primaryNeutral" onClick={onConfirm} disabled={isConfirmPending}>
                        {t('commons:confirm')}
                        {isConfirmPending && <Spinner size={14} />}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    )
}
